import React, { useState, useEffect } from 'react';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { 
  Link2, CheckCircle, XCircle, Clock, Loader2, BookOpen, FileText,
  Filter, Building2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPublisherAssociations = () => {
  const { token } = useAuth();
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: 'pending',
    content_type: ''
  });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchAssociations();
  }, [filter]);

  const fetchAssociations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status && filter.status !== 'all') params.append('status', filter.status);
      if (filter.content_type && filter.content_type !== 'all') params.append('content_type', filter.content_type);

      const res = await fetch(`${API_URL}/api/admin/publisher-associations?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssociations(data.associations || []);
      }
    } catch (err) {
      console.error('Error fetching associations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (contentType, contentId) => {
    setActionLoading(`approve-${contentId}`);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/publisher-associations/${contentType}/${contentId}/approve`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to approve');
      }
      
      toast.success('Association approved');
      fetchAssociations();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setActionLoading(`reject-${rejectModal.content_id}`);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/publisher-associations/${rejectModal.content_type}/${rejectModal.content_id}/reject`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ reason: rejectReason })
        }
      );
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to reject');
      }
      
      toast.success('Association rejected');
      setRejectModal(null);
      setRejectReason('');
      fetchAssociations();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="flex items-center gap-1 text-green-400 bg-green-950/50 px-2 py-1 rounded text-xs">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 text-red-400 bg-red-950/50 px-2 py-1 rounded text-xs">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-yellow-400 bg-yellow-950/50 px-2 py-1 rounded text-xs">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 p-6" data-testid="admin-associations-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Link2 className="w-6 h-6 text-purple-500" />
            Publisher Associations
          </h1>
          <p className="text-gray-400 text-sm">Review and manage series/novel publisher associations</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <Select
              value={filter.status}
              onValueChange={(val) => setFilter({ ...filter, status: val })}
            >
              <SelectTrigger className="w-40 bg-gray-900 border-gray-800">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All Statuses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Select
            value={filter.content_type}
            onValueChange={(val) => setFilter({ ...filter, content_type: val })}
          >
            <SelectTrigger className="w-40 bg-gray-900 border-gray-800">
              <SelectValue placeholder="Content Type" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="series">Comics Only</SelectItem>
              <SelectItem value="novel">Novels Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Associations List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : associations.length === 0 ? (
          <div className="text-center py-12 bg-gray-900 rounded-lg border border-gray-800">
            <Link2 className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">No associations found</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="associations-list">
            {associations.map((assoc) => (
              <div
                key={`${assoc.content_type}-${assoc.content_id}`}
                className="p-4 bg-gray-900 border border-gray-800 rounded-lg"
                data-testid={`assoc-${assoc.content_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Content Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gray-800 rounded flex items-center justify-center flex-shrink-0">
                      {assoc.content_type === 'series' ? (
                        <BookOpen className="w-5 h-5 text-purple-500" />
                      ) : (
                        <FileText className="w-5 h-5 text-purple-500" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white">{assoc.content_title}</span>
                        <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded capitalize">
                          {assoc.content_type}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">
                        By <span className="text-gray-300">{assoc.creator_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Publisher Info */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {assoc.publisher_logo_url ? (
                        <img 
                          src={assoc.publisher_logo_url} 
                          alt="" 
                          className="w-8 h-8 rounded object-cover" 
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-gray-600" />
                        </div>
                      )}
                      <span className="text-gray-300">{assoc.publisher_name}</span>
                    </div>
                    {getStatusBadge(assoc.status)}
                  </div>

                  {/* Actions */}
                  {assoc.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(assoc.content_type, assoc.content_id)}
                        disabled={actionLoading === `approve-${assoc.content_id}`}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-btn-${assoc.content_id}`}
                      >
                        {actionLoading === `approve-${assoc.content_id}` ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setRejectModal(assoc)}
                        data-testid={`reject-btn-${assoc.content_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>

                {/* Meta Info */}
                <div className="mt-3 pt-3 border-t border-gray-800 text-xs text-gray-500 flex flex-wrap gap-4">
                  <span>Requested: {new Date(assoc.requested_at).toLocaleString()}</span>
                  {assoc.approved_at && (
                    <span>Reviewed: {new Date(assoc.approved_at).toLocaleString()}</span>
                  )}
                  {assoc.rejected_reason && (
                    <span className="text-red-400">Reason: {assoc.rejected_reason}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reject Modal */}
        <Dialog open={!!rejectModal} onOpenChange={() => setRejectModal(null)}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle>Reject Association</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-400">
                Rejecting association for <strong className="text-white">{rejectModal?.content_title}</strong> with <strong className="text-white">{rejectModal?.publisher_name}</strong>
              </p>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Reason *</label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this association is being rejected..."
                  className="bg-gray-800 border-gray-700 min-h-[100px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectModal(null)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={actionLoading?.startsWith('reject')}
              >
                {actionLoading?.startsWith('reject') ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Reject Association'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPublisherAssociations;
